/**
 * shared/market_compare.ts — CLIENT MIRROR of server/src/lib/market_compare.ts (the server copy is authoritative; keep
 * them byte-identical below this header — `verify:market-compare` diffs them).
 */
import { marketUnitPrice } from './space_industry';
import { HULL_ORE_U, HULLS_MAX } from './space_economy';
import { EXCHANGE_GOODS, brokerFee, fillTax } from './space_catalog';

// ── LOCATED MARKETS, INDUSTRY AND FORECASTS (space campaign J, 2026-09-30; report §17.1–17.6) ──────────────────────
// Pure rules shared by the reducers and the dock: how the book crosses (Atlas's RESTING-price rule — never EVE's
// incoming-price rule), what a station's book summary says, a robust fill benchmark that refuses to invent a price from
// thin or self-made evidence, the route forecast (a forecast until a buyer is reserved), the courier collateral's
// certified basis, the foundry's process profiles, and the yard's health arithmetic. No Identity, no table — callers
// pass hex strings and plain numbers.

/** The rollout flag (ABSENT = ON): off → the new reducers refuse, the fill log / revision / summary / health stop. */
export const MARKET_COMPARE_FLAG = 'market_compare_v1';
/** Buy here crosses at most this many resting orders per action and says what it left (report §17.6). */
export const BUY_HERE_MAX_FILLS = 8;
/** The located-market sweep re-observes every station's book this often (the summary's AGE is measured from it). */
export const SUMMARY_EVERY_S = 300;
/** The fill benchmark looks back this far; fills are kept this long. */
export const FILL_WINDOW_S = 24 * 3600;
export const FILL_KEEP_S = 7 * 86_400;
/** Below this many independent buyer–seller pairs (or fills) a benchmark says "insufficient data" — never a price. */
export const BENCH_MIN_PAIRS = 3;
export const BENCH_MIN_FILLS = 3;
/** Price levels a summary keeps per side. */
export const SUMMARY_LEVELS = 3;
/** A summary older than this reads STALE on the panel (two missed sweeps). */
export const SUMMARY_STALE_S = 2 * SUMMARY_EVERY_S + 60;

// ── THE BOOK ─────────────────────────────────────────────────────────────────────────────────────────────────────
export interface BookOrder { orderId: bigint; ownerHex: string; side: string; familyKey: string; priceSov: bigint; qty: number }
export interface CrossFill { orderId: bigint; ownerHex: string; priceSov: bigint; qty: number }
export interface CrossPlan {
  fills: CrossFill[];
  /** Units the plan fills. */
  filled: number;
  /** Σ price × qty at the RESTING prices. */
  gross: bigint;
  /** What is left of the request. */
  remaining: number;
  /** True when the fill cap stopped the plan with more executable at the limit. */
  capped: boolean;
  /** Units the book would fill at the limit with no cap (excluding your own orders). */
  executable: number;
}

/** The crossing order placeMarketOrder uses: opposite side, same family, inside the limit, cheapest ask first (a buy)
 *  or richest bid first (a sell), ties by the older order id; never against yourself; every fill at the RESTING price. */
export function planCross(
  book: readonly BookOrder[], side: 'buy' | 'sell', familyKey: string, limit: bigint, qty: number, selfHex: string,
  maxFills = Number.POSITIVE_INFINITY,
): CrossPlan {
  const eligible = book
    .filter((o) => o.familyKey === familyKey && o.side !== side && o.qty > 0 && (side === 'buy' ? o.priceSov <= limit : o.priceSov >= limit))
    .sort((a, b) => (side === 'buy'
      ? (a.priceSov < b.priceSov ? -1 : a.priceSov > b.priceSov ? 1 : a.orderId < b.orderId ? -1 : 1)
      : (a.priceSov > b.priceSov ? -1 : a.priceSov < b.priceSov ? 1 : a.orderId < b.orderId ? -1 : 1)));
  const fills: CrossFill[] = [];
  let remaining = Math.max(0, Math.floor(qty)), gross = 0n, executable = 0, capped = false;
  for (const o of eligible) if (o.ownerHex !== selfHex) executable += o.qty;
  for (const o of eligible) {
    if (remaining <= 0) break;
    if (o.ownerHex === selfHex) continue; // never self-fill
    if (fills.length >= maxFills) { capped = true; break; }
    const fill = Math.min(remaining, o.qty);
    fills.push({ orderId: o.orderId, ownerHex: o.ownerHex, priceSov: o.priceSov, qty: fill });
    gross += o.priceSov * BigInt(fill);
    remaining -= fill;
  }
  return { fills, filled: Math.max(0, Math.floor(qty)) - remaining, gross, remaining, capped, executable };
}

export interface BookLevel { priceSov: bigint; units: number; orders: number }
export interface BookSummary {
  bestBid: bigint; bidUnits: number; bidOrders: number;
  bestAsk: bigint; askUnits: number; askOrders: number;
  bids: BookLevel[]; asks: BookLevel[];
}
/** One family's book at one station, summarised: best prices, total units, and the top levels per side. */
export function bookSummary(orders: readonly BookOrder[], levels = SUMMARY_LEVELS): BookSummary {
  const lv = (side: string, better: (a: bigint, b: bigint) => boolean): BookLevel[] => {
    const by = new Map<string, BookLevel>();
    for (const o of orders) {
      if (o.side !== side || o.qty <= 0) continue;
      const k = o.priceSov.toString();
      const l = by.get(k) ?? { priceSov: o.priceSov, units: 0, orders: 0 };
      l.units += o.qty; l.orders += 1; by.set(k, l);
    }
    return [...by.values()].sort((a, b) => (better(a.priceSov, b.priceSov) ? -1 : better(b.priceSov, a.priceSov) ? 1 : 0));
  };
  const bids = lv('buy', (a, b) => a > b), asks = lv('sell', (a, b) => a < b);
  const sum = (l: BookLevel[]): [number, number] => l.reduce((acc, x) => [acc[0] + x.units, acc[1] + x.orders], [0, 0]);
  const [bidUnits, bidOrders] = sum(bids), [askUnits, askOrders] = sum(asks);
  return {
    bestBid: bids[0]?.priceSov ?? 0n, bidUnits, bidOrders,
    bestAsk: asks[0]?.priceSov ?? 0n, askUnits, askOrders,
    bids: bids.slice(0, levels), asks: asks.slice(0, levels),
  };
}
/** Units executable from a summary's levels at a limit (buy: asks ≤ limit; sell: bids ≥ limit). */
export function levelsExecutable(levels: readonly BookLevel[], side: 'buy' | 'sell', limit: bigint): { units: number; gross: bigint } {
  let units = 0, gross = 0n;
  for (const l of levels) {
    if (side === 'buy' ? l.priceSov > limit : l.priceSov < limit) continue;
    units += l.units; gross += l.priceSov * BigInt(l.units);
  }
  return { units, gross };
}

// ── THE FILL BENCHMARK — robust, and honest when it does not know (report §17.5, §17.6) ──────────────────────────
export interface FillEvidence { priceSov: bigint; qty: number; buyerHex: string; sellerHex: string; internal: boolean; atS: number }
export interface Benchmark {
  /** Fills counted (self / internal ones excluded). */
  fills: number;
  /** Distinct buyer–seller pairs among them. */
  pairs: number;
  units: number;
  /** Fills left out: the same owner on both sides, or a guild vault and its own member. */
  excluded: number;
  /** Volume-weighted median price (0 when insufficient). */
  medianSov: bigint;
  /** Volume-weighted mean × 100 (0 when insufficient). */
  vwapX100: bigint;
  ok: boolean;
}
export function fillBenchmark(fills: readonly FillEvidence[], nowS: number, windowS = FILL_WINDOW_S): Benchmark {
  const used: FillEvidence[] = [];
  let excluded = 0;
  for (const f of fills) {
    if (f.atS < nowS - windowS || f.atS > nowS + 1 || f.qty <= 0) continue;
    if (f.buyerHex === f.sellerHex || f.internal) { excluded++; continue; }
    used.push(f);
  }
  const pairs = new Set(used.map((f) => (f.buyerHex < f.sellerHex ? `${f.buyerHex}|${f.sellerHex}` : `${f.sellerHex}|${f.buyerHex}`))).size;
  const units = used.reduce((n, f) => n + f.qty, 0);
  const ok = used.length >= BENCH_MIN_FILLS && pairs >= BENCH_MIN_PAIRS;
  if (!ok || units <= 0) return { fills: used.length, pairs, units, excluded, medianSov: 0n, vwapX100: 0n, ok: false };
  const sorted = [...used].sort((a, b) => (a.priceSov < b.priceSov ? -1 : a.priceSov > b.priceSov ? 1 : 0));
  let acc = 0, median = sorted[sorted.length - 1].priceSov;
  for (const f of sorted) { acc += f.qty; if (acc * 2 >= units) { median = f.priceSov; break; } }
  let value = 0n;
  for (const f of used) value += f.priceSov * BigInt(f.qty);
  return { fills: used.length, pairs, units, excluded, medianSov: median, vwapX100: (value * 100n) / BigInt(units), ok: true };
}

// ── THE ROUTE FORECAST (report §17.5: a forecast until an executable buyer is reserved) ──────────────────────────
export interface RouteIn {
  /** Executable units and their cost at the origin (from a plan or a summary). */
  buyUnits: number; buyGross: bigint;
  /** 'bid' = a funded bid placed at the origin (broker at placement); 'here' = Buy here (broker on what executed). */
  originMode: 'bid' | 'here';
  /** Where it sells: the destination book (a sell order: broker at placement + tax per fill) or the Exchange (no fee). */
  destMode: 'book' | 'exchange';
  sellUnits: number; sellUnitSov: bigint;
  holdFree: number;
  freightSov: bigint;
  /** Probability (0..1) the cargo is lost in transit — a declared input, never invented. */
  lossP: number;
  tradeLevel: number;
}
export interface RouteOut {
  units: number; cost: bigint; originBroker: bigint; revenue: bigint; tax: bigint; destBroker: bigint; freight: bigint;
  successNet: bigint; expectedNetX10: bigint; bound: 'origin' | 'destination' | 'hold' | 'none';
}
export function routeForecast(r: RouteIn): RouteOut {
  const units = Math.max(0, Math.min(r.buyUnits, r.sellUnits, r.holdFree));
  const bound: RouteOut['bound'] = units <= 0 ? 'none' : units === r.buyUnits ? 'origin' : units === r.sellUnits ? 'destination' : 'hold';
  const unitCostX = r.buyUnits > 0 ? r.buyGross : 0n;
  const cost = r.buyUnits > 0 ? (unitCostX * BigInt(units)) / BigInt(r.buyUnits) : 0n;
  const originBroker = units > 0 ? brokerFee(cost, r.tradeLevel) : 0n;
  const revenue = r.sellUnitSov * BigInt(units);
  const tax = r.destMode === 'book' ? fillTax(revenue) : 0n;
  const destBroker = r.destMode === 'book' && units > 0 ? brokerFee(revenue, r.tradeLevel) : 0n;
  const freight = units > 0 ? r.freightSov : 0n;
  const successNet = revenue - tax - destBroker - cost - originBroker - freight;
  // Expected (×10 to keep one decimal): the origin broker and the freight are sunk whatever happens; the destination
  // broker is paid only after the goods arrive (a destination ask cannot reserve cargo still in transit).
  const keepX1000 = BigInt(Math.round((1 - Math.max(0, Math.min(1, r.lossP))) * 1000));
  const expectedNetX10 = (keepX1000 * (revenue - tax - destBroker)) / 100n - (cost + originBroker + freight) * 10n;
  return { units, cost, originBroker, revenue, tax, destBroker, freight, successNet, expectedNetX10, bound };
}

// ── THE COURIER'S CERTIFIED BASIS (report §17.2: never a thin print) ───────────────────────────────────────────────
/** The catalog's ceiling drift: the 0.72–1.55 regional range × the rim's 1.25 premium for raw goods. */
export const CERTIFIED_DRIFT_CEIL = 1.55 * 1.25;
/** The most the Exchange's catalog mark can charge to buy one unit back (the yard's shortage and the 2 h depth are
 *  transient and excluded) — a conservative ceiling for collateral, read from the catalog, never from fills. */
export function certifiedUnitBasis(familyKey: string): bigint {
  const g = EXCHANGE_GOODS.find((x) => x.families.includes(familyKey));
  if (!g) return 0n;
  return marketUnitPrice(g.baseValue, CERTIFIED_DRIFT_CEIL, 'buy');
}
export function certifiedBasis(lines: readonly { familyKey: string; qty: number }[]): { basis: bigint; unknown: string[] } {
  let basis = 0n;
  const unknown: string[] = [];
  for (const l of lines) {
    const u = certifiedUnitBasis(l.familyKey);
    if (u <= 0n) unknown.push(l.familyKey);
    basis += u * BigInt(Math.max(0, Math.floor(l.qty)));
  }
  return { basis, unknown };
}

// ── THE FOUNDRY: ONE existing component process with profiles (report §17.4) ──────────────────────────────────────
/** The Metal Ingot (schematic `metal_ingot`: one slot, 20 × ferrite ore) run at a station's foundry line. */
export const FOUNDRY = {
  processKey: 'metal_ingot', name: 'Metal Ingot', version: 1,
  input: { familyKey: 'ferrite_ore', perRun: 20, reducible: true },
  runS: 90, maxRuns: 20,
  /** Running jobs a station's line holds at once; open jobs (reserved / running / ready) a pilot may hold. */
  linesPerStation: 4, openPerPilot: 2,
} as const;
export type ProfileKey = 'baseline' | 'throughput' | 'frugal';
export const PROFILES: Readonly<Record<ProfileKey, { label: string; timeMult: number; inputMult: number; brief: string }>> = {
  baseline: { label: 'Baseline', timeMult: 1, inputMult: 1, brief: 'the line as built' },
  throughput: { label: 'Throughput', timeMult: 0.9, inputMult: 1, brief: '10 % less time, the same inputs and quality' },
  frugal: { label: 'Frugal', timeMult: 1, inputMult: 0.98, brief: 'up to 2 % less reducible input across a batch (rounded up; a single input is never reduced)' },
};
export const isProfile = (k: string): k is ProfileKey => k === 'baseline' || k === 'throughput' || k === 'frugal';
/** EVE's job-total rule, adopted: max(runs × single, ceil(round2(runs × perRun × mult))) — rounding is per JOB, a whole
 *  single input stays one per product (W2E5–W2E8). */
export function batchInput(perRun: number, runs: number, mult: number, reducible: boolean): number {
  const n = Math.max(0, Math.floor(runs)), base = Math.max(0, Math.floor(perRun));
  if (!reducible || base <= 1 || mult >= 1) return base * n;
  const raw = Math.round(n * base * mult * 100) / 100;
  return Math.max(n, Math.ceil(raw));
}
export interface FabQuote {
  processKey: string; version: number; profile: ProfileKey; runs: number;
  inputFamily: string; inputQty: number; savedQty: number;
  durationS: number; outputs: number;
}
export function fabQuote(runs: number, profile: ProfileKey): FabQuote {
  const n = Math.max(1, Math.min(FOUNDRY.maxRuns, Math.floor(runs)));
  const p = PROFILES[profile];
  const inputQty = batchInput(FOUNDRY.input.perRun, n, p.inputMult, FOUNDRY.input.reducible);
  return {
    processKey: FOUNDRY.processKey, version: FOUNDRY.version, profile, runs: n,
    inputFamily: FOUNDRY.input.familyKey, inputQty, savedQty: FOUNDRY.input.perRun * n - inputQty,
    durationS: Math.ceil(n * FOUNDRY.runS * p.timeMult), outputs: n,
  };
}
/** Runs finished after `elapsedS` of a running job (they finish evenly through it). */
export function runsDoneAt(elapsedS: number, durationS: number, runs: number): number {
  if (durationS <= 0) return runs;
  return Math.max(0, Math.min(runs, Math.floor((Math.max(0, elapsedS) * runs) / durationS)));
}
export type FabState = 'reserved' | 'running' | 'ready' | 'collected' | 'cancelled';
/** THE CANCELLATION RULE, stated before a job is reserved: queued → every input back untouched; running → the runs
 *  already finished are yours and the rest of the input was consumed when the line started; ready → collect instead. */
export function cancelOutcome(state: FabState, runsDone: number, runs: number, inputQty: number): { allowed: boolean; returnInput: number; outputs: number; lostInput: number } {
  if (state === 'reserved') return { allowed: true, returnInput: inputQty, outputs: 0, lostInput: 0 };
  if (state === 'running') {
    const out = Math.max(0, Math.min(runs, runsDone));
    return { allowed: out < runs, returnInput: 0, outputs: out, lostInput: inputQty };
  }
  return { allowed: false, returnInput: 0, outputs: 0, lostInput: 0 };
}
/** The bench's assembly band for a ceiling (55–70 %): the line never botches and never amazes. */
export function qualityBand(ceiling: number): [number, number] {
  return [Math.max(1, Math.round(ceiling * 0.55)), Math.max(1, Math.round(ceiling * 0.7))];
}

// ── THE YARD'S HEALTH (report §17.5: usable is not pending; no invented confidence) ───────────────────────────────
export interface YardHealthIn { ore: number; hulls: number; target: number; cash: bigint; markSov: bigint; pendingUnits: number }
export interface YardHealth {
  usable: number;
  /** Ore the next hull would draw (0 at the cap: a full yard eats nothing — THE FULL-YARD LAW). */
  reservedNext: number;
  /** Ore short of the next hull (0 when it has 40 or is at the cap). */
  gapNext: number;
  /** Ore needed to bring the hulls up to the target (after what is on hand). */
  demand: number;
  /** Units the yard's cash buys at its own live mark. */
  affordable: number;
  funded: number; unfunded: number;
  /** In flight to it — pending coverage, never usable. */
  pending: number;
  atCap: boolean;
}
export function yardHealth(i: YardHealthIn): YardHealth {
  const atCap = i.hulls >= HULLS_MAX;
  const usable = Math.max(0, Math.floor(i.ore));
  const reservedNext = atCap ? 0 : Math.min(usable, HULL_ORE_U);
  const gapNext = atCap ? 0 : Math.max(0, HULL_ORE_U - usable);
  const want = Math.max(0, Math.min(i.target, HULLS_MAX) - i.hulls) * HULL_ORE_U;
  const demand = Math.max(0, want - usable);
  const affordable = i.markSov > 0n && i.cash > 0n ? Number(i.cash / i.markSov) : 0;
  const funded = Math.min(demand, affordable);
  return { usable, reservedNext, gapNext, demand, affordable, funded, unfunded: demand - funded, pending: Math.max(0, i.pendingUnits), atCap };
}
